// The staff app's parts. Pages build from these so every table, button, date and confirm behaves one way.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { ApiError } from '../../../lib'
import { ageSeconds, ago, since, when, type When } from './time'
export * from './time'

/** Refresh the "ago" words every 15 seconds without a refetch. */
function useTick(ms = 15_000): number {
  const [n, set] = useState(Date.now())
  useEffect(() => { const t = setInterval(() => set(Date.now()), ms); return () => clearInterval(t) }, [ms])
  return n
}

export function Page({ title, sub, right, children, testid }: { title: ReactNode; sub?: ReactNode; right?: ReactNode; children: ReactNode; testid?: string }) {
  useEffect(() => { if (typeof title === 'string') document.title = `${title} | ernd staff` }, [title])
  return (
    <div className="st-page" data-testid={testid}>
      <header className="st-head">
        <div><h1 className="st-title">{title}</h1>{sub ? <div className="st-sub">{sub}</div> : null}</div>
        {right ? <div className="st-head-right">{right}</div> : null}
      </header>
      {children}
    </div>
  )
}

export function Section({ id, title, count, right, children, testid }: { id?: string; title: ReactNode; count?: ReactNode; right?: ReactNode; children: ReactNode; testid?: string }) {
  return (
    <section id={id} className="st-section" data-testid={testid}>
      <div className="st-section-head">
        <h2 className="st-section-title">{title}{count !== undefined && count !== null ? <span>{count}</span> : null}</h2>
        {right}
      </div>
      {children}
    </section>
  )
}

/** A read time, always. `label` names the source. Past `limit` seconds, or with no time at all, it turns red. */
export function Fresh({ at, label, limit, live, testid }: { at: When; label?: string; limit?: number; live?: boolean; testid?: string }) {
  const now = Math.max(useTick(), Date.now())   // a fetch newer than the last tick never reads as "in 2s"
  const age = ageSeconds(at, now)
  const future = age !== null && age < -300        // a read time ahead of the clock can't be trusted as current
  const stale = age === null || future || (limit !== undefined && age > limit)
  const word = !at ? 'not read' : future ? `dated ${since(at, now)}` : live ? `read ${since(at, now)}` : `${ago(at, now)} old`
  return <span className={`st-fresh${stale ? ' stale' : ''}`} title={at ? when(at) : 'Never read'} data-testid={testid}>{label ? `${label} ` : ''}{word}</span>
}

export function Sources({ children }: { children: ReactNode }) {
  return <div className="st-sources">{children}</div>
}

/** A number nobody measured is a dash. A true zero is 0. */
export const num = (v: number | null | undefined): string => (v === null || v === undefined ? '–' : v.toLocaleString('en-US'))
export const pct = (n: number | null | undefined, d: number | null | undefined): string =>
  n === null || n === undefined || !d ? '–' : `${Math.round((n / d) * 1000) / 10}%`

export function Button({ tone, keyHint, className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'danger' | 'quiet'; keyHint?: string }) {
  return (
    <button type="button" className={`st-btn${tone ? ` ${tone}` : ''}${className ? ` ${className}` : ''}`} {...rest}>
      {rest.children}{keyHint ? <kbd>{keyHint}</kbd> : null}
    </button>
  )
}

export function Copy({ text, label = 'Copy', onCopied, testid }: { text: string; label?: string; onCopied?: () => void; testid?: string }) {
  const [done, setDone] = useState<'' | 'Copied' | 'Copy failed'>('')
  return (
    <button type="button" className="st-btn quiet" data-testid={testid} onClick={async () => {
      const ok = await copyText(text)
      setDone(ok ? 'Copied' : 'Copy failed')
      if (ok) onCopied?.()
      setTimeout(() => setDone(''), 1200)
    }}>{done || label}</button>
  )
}

/** True only when the text reached the clipboard. The browser can refuse, for one when another tab has focus. */
export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true } catch { return false }
}

/** Copy, then say what really happened. */
export function useCopy() {
  const { toast } = useKit()
  return async (text: string, done: string) => {
    const ok = await copyText(text)
    toast(ok ? done : 'Copy failed. Select the text by hand', { bad: !ok })
    return ok
  }
}

export const errorText = (e: unknown): string =>
  e instanceof ApiError ? e.message || e.code : e instanceof Error ? e.message : 'Something went wrong'

export const Busy = ({ what = 'Loading' }: { what?: string }) => <p className="st-note" role="status">{what}</p>
export const Err = ({ error }: { error: unknown }) => <div className="st-banner bad" role="alert">{errorText(error)}</div>
export const Empty = ({ children }: { children: ReactNode }) => <p className="st-note">{children}</p>

export type Column<T> = {
  key: string
  head: ReactNode
  cell: (row: T) => ReactNode
  num?: boolean
  primary?: boolean        // the row's title on a phone
  phone?: boolean          // false: left off the stacked row on a phone
  label?: string           // the phone label, when head isn't text
  nowrap?: boolean
  more?: boolean           // hidden on a phone: the row opens to show it
}

/** A dense table. `onOpen` makes rows clickable. `cur` marks the keyboard row. On a phone it stacks unless `flat`. */
export function Table<T>({ rows, columns, rowKey, onOpen, onPick, cur, empty, testid, flat, rowAttrs }: {
  rows: T[]; columns: Column<T>[]; rowKey: (r: T) => string; onOpen?: (r: T) => void; cur?: string | null; empty?: ReactNode; testid?: string; flat?: boolean
  onPick?: (r: T) => void   // any click on the row, a link in it included, so Back returns to the row you left
  rowAttrs?: (r: T) => Record<string, string | boolean | undefined>   // data-testid and data-* on each row, so old selectors keep working
}) {
  if (!rows.length) return <Empty>{empty ?? 'Nothing here.'}</Empty>
  return (
    <div className="st-table-wrap">
      <table className={`st-table${flat ? '' : ' stack'}`} data-testid={testid}>
        <thead><tr>{columns.map((c) => <th key={c.key} className={c.num ? 'st-num' : undefined}>{c.head}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => {
            const k = rowKey(r)
            return (
              <tr key={k} data-row={k} {...rowAttrs?.(r)} className={`${onOpen ? 'open' : ''}${cur === k ? ' cur' : ''}`} onClick={onOpen || onPick ? (e) => {
                onPick?.(r)
                if ((e.target as HTMLElement).closest('a,button,input,select,textarea,label')) return
                onOpen?.(r)
              } : undefined}>
                {columns.map((c) => {
                  const v = c.cell(r)
                  const empt = v === null || v === undefined || v === '' || v === false
                  return <td key={c.key} data-label={c.label ?? (typeof c.head === 'string' ? c.head : '')} className={[c.num ? 'st-num' : '', c.num && /^\d+$/.test(String(v)) ? 'st-tnum' : '', c.primary ? 'st-primary' : '', c.more ? 'st-phone-more' : '', empt ? 'st-empty' : '', c.nowrap ? 'st-nowrap' : '', c.phone === false ? 'st-desk' : ''].filter(Boolean).join(' ') || undefined}>{empt ? null : v}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function Strip<K extends string>({ items, value, onChange, testid }: { items: { key: K; label: string; n?: number | null }[]; value: K; onChange: (k: K) => void; testid?: string }) {
  return (
    <div className="st-strip" role="tablist" data-testid={testid}>
      {items.map((i) => (
        <button key={i.key} type="button" role="tab" aria-selected={i.key === value} className={i.key === value ? 'on' : undefined} data-testid={testid ? `${testid}-${i.key}` : undefined} onClick={() => onChange(i.key)}>
          {i.label}{i.n !== undefined ? <span>{num(i.n)}</span> : null}
        </button>
      ))}
    </div>
  )
}

const typing = (t: EventTarget | null) => {
  const el = t as HTMLElement | null
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)
}

/** A #anchor on a page whose rows arrive after it loads. `ready` says the rows are in. */
export function useHashScroll(ready: boolean) {
  const { hash } = useLocation()
  useEffect(() => {
    if (!ready || !hash) return
    const t = setTimeout(() => document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: 'start' }), 40)
    return () => clearTimeout(t)
  }, [ready, hash])
}

/** Keys for a page. Ignored while typing, while a dialog is open, and with a modifier held. */
export function useKeys(keys: Record<string, (e: KeyboardEvent) => void>, on = true) {
  const ref = useRef(keys)
  ref.current = keys
  useEffect(() => {
    if (!on) return
    const fn = (e: KeyboardEvent) => {
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey || document.querySelector('.st-scrim')) return
      if (e.key === 'Enter' && (e.target as HTMLElement).closest?.('a,button,summary')) return   // Enter on a focused link follows it
      const f = ref.current[e.key]
      if (f) { e.preventDefault(); f(e) }
    }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [on])
}

/** j, k and the arrows over a list. Keeps the current row by its key, so a row leaving never slides the cursor onto its
 * neighbour: the current row becomes null and the next key picks a row again. */
export function useCursor<T>(rows: T[], rowKey: (r: T) => string, onOpen?: (r: T) => void) {
  const store = `st-cur:${window.location.pathname}${window.location.search}`
  const [cur, set] = useState<string | null>(() => { try { return sessionStorage.getItem(store) } catch { return null } })
  const setCur = (k: string | null) => {
    set(k)
    try { if (k) sessionStorage.setItem(store, k); else sessionStorage.removeItem(store) } catch { /* the cursor stays for this visit only */ }
  }
  const present = cur !== null && rows.some((r) => rowKey(r) === cur)
  const move = (d: number) => {
    if (!rows.length) return
    const i = rows.findIndex((r) => rowKey(r) === cur)
    const n = i < 0 ? 0 : Math.max(0, Math.min(rows.length - 1, i + d))
    const k = rowKey(rows[n])
    setCur(k)
    document.querySelector(`[data-row="${CSS.escape(k)}"]`)?.scrollIntoView({ block: 'nearest' })
  }
  useKeys({ j: () => move(1), k: () => move(-1), ArrowDown: () => move(1), ArrowUp: () => move(-1), Enter: () => { const r = rows.find((x) => rowKey(x) === cur); if (r && onOpen) onOpen(r) } })
  const row = present ? rows.find((r) => rowKey(r) === cur) ?? null : null
  return { cur: present ? cur : null, row, setCur }
}

type ToastT = { id: number; text: string; bad?: boolean; action?: { label: string; run: () => void } }
type ConfirmT = { title: string; body?: ReactNode; confirm?: string; danger?: boolean; reason?: { label: string; required?: boolean }; resolve: (v: { ok: boolean; reason?: string }) => void }
type Kit = {
  toast: (text: string, o?: { bad?: boolean; action?: ToastT['action']; ms?: number }) => void
  confirm: (o: Omit<ConfirmT, 'resolve'>) => Promise<{ ok: boolean; reason?: string }>
}
const KitCtx = createContext<Kit | null>(null)
export function useKit(): Kit {
  const k = useContext(KitCtx)
  if (!k) throw new Error('useKit outside the staff app')
  return k
}

/** Toasts and the one confirm dialog. A money action calls confirm() and states the amount and who. */
export function KitProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastT[]>([])
  const [dlg, setDlg] = useState<ConfirmT | null>(null)
  const seq = useRef(0)
  const toast = useCallback<Kit['toast']>((text, o) => {
    const id = ++seq.current
    setToasts((t) => [...t, { id, text, bad: o?.bad, action: o?.action }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), o?.ms ?? (o?.action ? 10_000 : 3_500))
  }, [])
  const confirm = useCallback<Kit['confirm']>((o) => new Promise((resolve) => setDlg({ ...o, resolve })), [])
  const value = useMemo(() => ({ toast, confirm }), [toast, confirm])
  return (
    <KitCtx.Provider value={value}>
      {children}
      {dlg ? <ConfirmDialog d={dlg} done={(v) => { dlg.resolve(v); setDlg(null) }} /> : null}
      <div className="st-toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`st-toast${t.bad ? ' bad' : ''}`} data-testid="st-toast">
            <span>{t.text}</span>
            {t.action ? <button type="button" onClick={() => { t.action!.run(); setToasts((x) => x.filter((y) => y.id !== t.id)) }}>{t.action.label}</button> : null}
          </div>
        ))}
      </div>
    </KitCtx.Provider>
  )
}

function ConfirmDialog({ d, done }: { d: ConfirmT; done: (v: { ok: boolean; reason?: string }) => void }) {
  const [reason, setReason] = useState('')
  const ready = !d.reason?.required || reason.trim().length >= 3
  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') done({ ok: false }) }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  })
  return (
    <div className="st-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) done({ ok: false }) }}>
      <div className="st-dialog" role="dialog" aria-modal="true" aria-label={d.title} data-testid="st-confirm">
        <h2>{d.title}</h2>
        {d.body ? <div className="st-muted">{d.body}</div> : null}
        {d.reason ? (
          <label className="st-field">{d.reason.label}
            <textarea className="st-text" autoFocus value={reason} data-testid="st-confirm-reason" onChange={(e) => setReason(e.target.value)} />
          </label>
        ) : null}
        <div className="st-dialog-foot">
          <Button tone="quiet" onClick={() => done({ ok: false })} data-testid="st-confirm-no">Cancel</Button>
          <Button autoFocus={!d.reason} tone={d.danger ? 'danger' : 'primary'} disabled={!ready} onClick={() => done({ ok: true, reason: reason.trim() || undefined })} data-testid="st-confirm-yes">{d.confirm ?? 'Confirm'}</Button>
        </div>
      </div>
    </div>
  )
}
